import { GroceryItem, StorageLocation, Recipe, ShoppingListItem, WasteRecord } from '../types';

export const DEFAULT_LOCATIONS: StorageLocation[] = [
  {
    id: 'loc-fridge',
    name: 'Main Refrigerator',
    type: 'fridge',
    icon: 'Refrigerator',
    description: 'Fresh dairy, meats, produce, and opened sauces',
    temperature: '3.5°C / 38°F',
    grocyLocationId: 1,
  },
  {
    id: 'loc-freezer',
    name: 'Deep Freezer',
    type: 'freezer',
    icon: 'Snowflake',
    description: 'Long-term frozen meats, vegetables, ice cream',
    temperature: '-18°C / 0°F',
    grocyLocationId: 2,
  },
  {
    id: 'loc-pantry',
    name: 'Kitchen Pantry',
    type: 'pantry',
    icon: 'Package',
    description: 'Dry goods, grains, canned items, oils & snacks',
    temperature: '20°C / 68°F',
    grocyLocationId: 3,
  },
  {
    id: 'loc-spice',
    name: 'Spice & Herb Rack',
    type: 'spice_rack',
    icon: 'Sparkles',
    description: 'Dried herbs, ground spices, seasoning blends',
    temperature: '20°C / 68°F',
    grocyLocationId: 4,
  },
  {
    id: 'loc-counter',
    name: 'Fruit Bowl & Counter',
    type: 'counter',
    icon: 'Apple',
    description: 'Bananas, tomatoes, fresh citrus & daily bread',
    temperature: '21°C / 70°F',
    grocyLocationId: 5,
  },
];

// Helper to get relative date string YYYY-MM-DD
export function getDateOffset(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
}

export const INITIAL_GROCERY_ITEMS: GroceryItem[] = [
  {
    id: 'item-1',
    name: 'Organic Whole Milk',
    brand: 'Horizon Organic',
    barcode: '074236521401',
    category: 'Dairy & Eggs',
    locationId: 'loc-fridge',
    quantity: 1,
    unit: 'Bottle (1 Gallon)',
    minStockAmount: 1,
    expirationDate: getDateOffset(2), // Expiring soon!
    purchasedDate: getDateOffset(-5),
    openedDate: getDateOffset(-3),
    isOpened: true,
    price: 4.89,
    imageUrl: 'https://images.unsplash.com/photo-1550583724-b2692b85b150?w=500&auto=format&fit=crop&q=80',
    calories: 150,
    grocyProductId: 101,
    grocyStockId: 201,
  },
  {
    id: 'item-2',
    name: 'Pasture-Raised Large Eggs',
    brand: 'Vital Farms',
    barcode: '852398004012',
    category: 'Dairy & Eggs',
    locationId: 'loc-fridge',
    quantity: 10,
    unit: 'pcs (12-pack)',
    minStockAmount: 4,
    expirationDate: getDateOffset(12),
    purchasedDate: getDateOffset(-2),
    price: 5.99,
    imageUrl: 'https://images.unsplash.com/photo-1582722872445-44dc5f7e3c8f?w=500&auto=format&fit=crop&q=80',
    calories: 70,
    grocyProductId: 102,
    grocyStockId: 202,
  },
  {
    id: 'item-3',
    name: 'Greek Whole Milk Yogurt',
    brand: 'FAGE Total 5%',
    barcode: '689544081005',
    category: 'Dairy & Eggs',
    locationId: 'loc-fridge',
    quantity: 2,
    unit: 'Tub (500g)',
    minStockAmount: 1,
    expirationDate: getDateOffset(4),
    purchasedDate: getDateOffset(-4),
    price: 4.49,
    imageUrl: 'https://images.unsplash.com/photo-1488477181946-6428a0291777?w=500&auto=format&fit=crop&q=80',
    calories: 130,
    grocyProductId: 103,
    grocyStockId: 203,
  },
  {
    id: 'item-4',
    name: 'Fresh Hass Avocados',
    brand: 'NatureFresh',
    category: 'Produce',
    locationId: 'loc-counter',
    quantity: 3,
    unit: 'pcs',
    minStockAmount: 2,
    expirationDate: getDateOffset(1), // Urgent!
    purchasedDate: getDateOffset(-4),
    price: 3.99,
    imageUrl: 'https://images.unsplash.com/photo-1523049673857-eb18f1d7b578?w=500&auto=format&fit=crop&q=80',
    calories: 240,
    grocyProductId: 104,
    grocyStockId: 204,
  },
  {
    id: 'item-5',
    name: 'Organic Baby Spinach',
    brand: 'Earthbound Farm',
    barcode: '032601950012',
    category: 'Produce',
    locationId: 'loc-fridge',
    quantity: 1,
    unit: 'Clamshell (300g)',
    minStockAmount: 1,
    expirationDate: getDateOffset(-1), // Expired yesterday
    purchasedDate: getDateOffset(-7),
    isOpened: true,
    price: 3.49,
    imageUrl: 'https://images.unsplash.com/photo-1576045057995-568f588f82fb?w=500&auto=format&fit=crop&q=80',
    calories: 25,
    grocyProductId: 105,
    grocyStockId: 205,
  },
  {
    id: 'item-6',
    name: 'Boneless Chicken Breasts',
    brand: 'Bell & Evans',
    barcode: '071421008921',
    category: 'Meat & Seafood',
    locationId: 'loc-freezer',
    quantity: 4,
    unit: 'fillets (800g)',
    minStockAmount: 2,
    expirationDate: getDateOffset(60),
    purchasedDate: getDateOffset(-10),
    isFrozen: true,
    price: 11.50,
    imageUrl: 'https://images.unsplash.com/photo-1604503468506-a8da13d82791?w=500&auto=format&fit=crop&q=80',
    calories: 165,
    grocyProductId: 106,
    grocyStockId: 206,
  },
  {
    id: 'item-7',
    name: 'Artisan Sourdough Loaf',
    brand: 'Local Bakery',
    category: 'Bakery',
    locationId: 'loc-counter',
    quantity: 1,
    unit: 'Loaf (600g)',
    minStockAmount: 1,
    expirationDate: getDateOffset(3),
    purchasedDate: getDateOffset(-2),
    price: 5.50,
    imageUrl: 'https://images.unsplash.com/photo-1589367920969-ab8e050bbb04?w=500&auto=format&fit=crop&q=80',
    calories: 220,
    grocyProductId: 107,
    grocyStockId: 207,
  },
  {
    id: 'item-8',
    name: 'Extra Virgin Olive Oil',
    brand: 'California Olive Ranch',
    barcode: '850687002018',
    category: 'Pantry & Grains',
    locationId: 'loc-pantry',
    quantity: 1,
    unit: 'Bottle (750ml)',
    minStockAmount: 1,
    expirationDate: getDateOffset(240),
    purchasedDate: getDateOffset(-30),
    price: 14.99,
    imageUrl: 'https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?w=500&auto=format&fit=crop&q=80',
    calories: 120,
    grocyProductId: 108,
    grocyStockId: 208,
  },
  {
    id: 'item-9',
    name: 'Royal Jasmine Rice',
    brand: 'Three Ladies',
    barcode: '085265001201',
    category: 'Pantry & Grains',
    locationId: 'loc-pantry',
    quantity: 3.5,
    unit: 'kg',
    minStockAmount: 1,
    expirationDate: getDateOffset(365),
    purchasedDate: getDateOffset(-45),
    price: 9.99,
    imageUrl: 'https://images.unsplash.com/photo-1586201375761-83865001e31c?w=500&auto=format&fit=crop&q=80',
    calories: 160,
    grocyProductId: 109,
    grocyStockId: 209,
  },
  {
    id: 'item-10',
    name: 'Bronze-Cut Rigatoni Pasta',
    brand: 'De Cecco',
    barcode: '024094070246',
    category: 'Pantry & Grains',
    locationId: 'loc-pantry',
    quantity: 2,
    unit: 'Boxes (500g)',
    minStockAmount: 2,
    expirationDate: getDateOffset(400),
    purchasedDate: getDateOffset(-15),
    price: 3.29,
    imageUrl: 'https://images.unsplash.com/photo-1551462147-ff29053bfc14?w=500&auto=format&fit=crop&q=80',
    calories: 200,
    grocyProductId: 110,
    grocyStockId: 210,
  },
  {
    id: 'item-11',
    name: 'San Marzano Canned Tomatoes',
    brand: 'Rega DOP',
    barcode: '800539000102',
    category: 'Canned Goods',
    locationId: 'loc-pantry',
    quantity: 3,
    unit: 'Cans (800g)',
    minStockAmount: 2,
    expirationDate: getDateOffset(500),
    purchasedDate: getDateOffset(-20),
    price: 4.25,
    imageUrl: 'https://images.unsplash.com/photo-1592924357228-91a4daadcfea?w=500&auto=format&fit=crop&q=80',
    calories: 30,
    grocyProductId: 111,
    grocyStockId: 211,
  },
  {
    id: 'item-12',
    name: 'Aged Parmigiano Reggiano',
    brand: 'Zanetti 24-Month',
    barcode: '071295034102',
    category: 'Dairy & Eggs',
    locationId: 'loc-fridge',
    quantity: 1,
    unit: 'Wedge (250g)',
    minStockAmount: 1,
    expirationDate: getDateOffset(45),
    purchasedDate: getDateOffset(-8),
    isOpened: true,
    price: 8.99,
    imageUrl: 'https://images.unsplash.com/photo-1452195100486-9cc805987862?w=500&auto=format&fit=crop&q=80',
    calories: 110,
    grocyProductId: 112,
    grocyStockId: 212,
  },
];

export const INITIAL_RECIPES: Recipe[] = [
  {
    id: 'rec-1',
    name: 'Creamy Garlic Chicken Rigatoni',
    description: 'Rich, savory pasta with golden pan-seared chicken breast, parmesan cream sauce, and fresh baby spinach.',
    prepTimeMinutes: 10,
    cookTimeMinutes: 20,
    servings: 4,
    difficulty: 'Easy',
    caloriesPerServing: 560,
    imageUrl: 'https://images.unsplash.com/photo-1621996346565-e3d5d6281297?w=600&auto=format&fit=crop&q=80',
    category: 'Dinner',
    ingredients: [
      { name: 'Boneless Chicken Breasts', amount: 2, unit: 'fillets', required: true, matchedItemId: 'item-6', isAvailable: true },
      { name: 'Bronze-Cut Rigatoni Pasta', amount: 300, unit: 'g', required: true, matchedItemId: 'item-10', isAvailable: true },
      { name: 'Organic Baby Spinach', amount: 100, unit: 'g', required: true, matchedItemId: 'item-5', isAvailable: true },
      { name: 'Aged Parmigiano Reggiano', amount: 50, unit: 'g', required: true, matchedItemId: 'item-12', isAvailable: true },
      { name: 'Organic Whole Milk', amount: 150, unit: 'ml', required: true, matchedItemId: 'item-1', isAvailable: true },
      { name: 'Extra Virgin Olive Oil', amount: 2, unit: 'tbsp', required: true, matchedItemId: 'item-8', isAvailable: true },
      { name: 'Garlic cloves', amount: 3, unit: 'cloves', required: false, isAvailable: false },
    ],
    instructions: [
      'Boil a large pot of salted water and cook Rigatoni pasta until al dente (about 10 mins).',
      'Season chicken breasts with salt, pepper, and garlic powder. Heat olive oil in a skillet over medium-high heat and sear chicken for 6-7 mins per side until golden.',
      'Remove chicken, slice into strips. In the same pan, reduce heat, add splash of milk, shredded parmesan, and stir until creamy.',
      'Fold in baby spinach and cooked pasta until spinach is gently wilted.',
      'Top with chicken slices and extra parmesan cheese before serving.',
    ],
    tags: ['High Protein', 'Pantry Friendly', 'Quick Dinner'],
  },
  {
    id: 'rec-2',
    name: 'Artisan Avocado Toast with Poached Egg',
    description: 'Crispy toasted sourdough bread topped with creamy mashed avocado, chili flakes, and a delicate runny egg.',
    prepTimeMinutes: 5,
    cookTimeMinutes: 5,
    servings: 2,
    difficulty: 'Easy',
    caloriesPerServing: 380,
    imageUrl: 'https://images.unsplash.com/photo-1525351484163-7529414344d8?w=600&auto=format&fit=crop&q=80',
    category: 'Breakfast & Brunch',
    ingredients: [
      { name: 'Artisan Sourdough Loaf', amount: 2, unit: 'slices', required: true, matchedItemId: 'item-7', isAvailable: true },
      { name: 'Fresh Hass Avocados', amount: 1, unit: 'pcs', required: true, matchedItemId: 'item-4', isAvailable: true },
      { name: 'Pasture-Raised Large Eggs', amount: 2, unit: 'pcs', required: true, matchedItemId: 'item-2', isAvailable: true },
      { name: 'Extra Virgin Olive Oil', amount: 1, unit: 'tsp', required: false, matchedItemId: 'item-8', isAvailable: true },
      { name: 'Red pepper flakes & Sea Salt', amount: 1, unit: 'pinch', required: false, isAvailable: true },
    ],
    instructions: [
      'Toast sourdough slices until deeply golden and crisp.',
      'In a small bowl, coarsely mash the avocado with a drizzle of olive oil, salt, and lemon juice.',
      'Poach or soft-fry the eggs in a pan until whites are set and yolks are runny.',
      'Spread avocado generously onto toast, crown with warm egg, and sprinkle with red pepper flakes.',
    ],
    tags: ['Breakfast', 'Superfood', 'Quick & Easy', 'Zero Waste Alert'],
  },
  {
    id: 'rec-3',
    name: 'Authentic San Marzano Tomato Pasta',
    description: 'Classic Neapolitan quick tomato sauce made with sweet San Marzano tomatoes, olive oil, and fresh basil.',
    prepTimeMinutes: 5,
    cookTimeMinutes: 15,
    servings: 3,
    difficulty: 'Easy',
    caloriesPerServing: 420,
    imageUrl: 'https://images.unsplash.com/photo-1563379091339-03b21ab4a4f8?w=600&auto=format&fit=crop&q=80',
    category: 'Dinner',
    ingredients: [
      { name: 'San Marzano Canned Tomatoes', amount: 1, unit: 'Can (800g)', required: true, matchedItemId: 'item-11', isAvailable: true },
      { name: 'Bronze-Cut Rigatoni Pasta', amount: 250, unit: 'g', required: true, matchedItemId: 'item-10', isAvailable: true },
      { name: 'Extra Virgin Olive Oil', amount: 3, unit: 'tbsp', required: true, matchedItemId: 'item-8', isAvailable: true },
      { name: 'Aged Parmigiano Reggiano', amount: 40, unit: 'g', required: false, matchedItemId: 'item-12', isAvailable: true },
    ],
    instructions: [
      'Heat olive oil in a pan over medium heat with a crushed clove of garlic.',
      'Crush San Marzano tomatoes by hand and add to pan. Simmer for 15 minutes until fragrant and slightly reduced.',
      'Toss hot pasta directly into the sauce and finish with fresh parmesan.',
    ],
    tags: ['Vegetarian', 'Classic Italian', 'Pantry Staple'],
  },
];

export const INITIAL_SHOPPING_LIST: ShoppingListItem[] = [
  {
    id: 'shop-1',
    name: 'Fresh Garlic Bulbs',
    category: 'Produce',
    quantity: 3,
    unit: 'heads',
    checked: false,
    priority: 'medium',
    estimatedPrice: 1.50,
    notes: 'For pasta and chicken marinades',
  },
  {
    id: 'shop-2',
    name: 'Oat Milk (Barista Edition)',
    category: 'Beverages',
    quantity: 2,
    unit: 'Cartons (1L)',
    checked: false,
    autoAdded: false,
    priority: 'urgent',
    estimatedPrice: 4.29,
  },
  {
    id: 'shop-3',
    name: 'Fresh Basil Herb Bunch',
    category: 'Produce',
    quantity: 1,
    unit: 'pack',
    checked: false,
    priority: 'low',
    estimatedPrice: 2.19,
  },
  {
    id: 'shop-4',
    name: 'Sparkling Mineral Water',
    category: 'Beverages',
    quantity: 1,
    unit: '12-Pack Cans',
    checked: true,
    priority: 'low',
    estimatedPrice: 6.99,
  },
];

export const INITIAL_WASTE_RECORDS: WasteRecord[] = [
  {
    id: 'waste-1',
    itemName: 'Fresh Strawberries',
    category: 'Produce',
    quantity: 1,
    unit: 'punnet (250g)',
    reason: 'spoiled',
    date: getDateOffset(-6),
    estimatedLoss: 3.99,
  },
  {
    id: 'waste-2',
    itemName: 'Sour Cream (Light)',
    category: 'Dairy & Eggs',
    quantity: 0.5,
    unit: 'Tub',
    reason: 'expired',
    date: getDateOffset(-12),
    estimatedLoss: 1.50,
  },
];

// Sample simulated vision recognition catalogs for instant instant photo analysis
export const SAMPLE_AI_PRESETS = [
  {
    title: '🛒 Fresh Grocery Haul (Farmer Market & Counter)',
    summary: 'Detected 4 fresh grocery items with estimated quantities and shelf-life recommendations.',
    items: [
      {
        id: 'det-1',
        name: 'Organic Honeycrisp Apples',
        brand: 'Washington Orchards',
        category: 'Produce' as const,
        suggestedLocationId: 'loc-counter',
        quantity: 5,
        unit: 'pcs (approx 1kg)',
        estimatedShelfLifeDays: 14,
        confidence: 0.96,
        expirationDate: getDateOffset(14),
        estimatedPrice: 4.99,
        selected: true,
        matchedGrocyProductName: 'Honeycrisp Apples',
      },
      {
        id: 'det-2',
        name: 'Greek Whole Milk Yogurt',
        brand: 'FAGE Total 5%',
        category: 'Dairy & Eggs' as const,
        suggestedLocationId: 'loc-fridge',
        quantity: 2,
        unit: 'Tub (500g)',
        estimatedShelfLifeDays: 18,
        confidence: 0.98,
        expirationDate: getDateOffset(18),
        estimatedPrice: 4.49,
        selected: true,
        matchedGrocyProductId: 103,
        matchedGrocyProductName: 'Greek Whole Milk Yogurt',
      },
      {
        id: 'det-3',
        name: 'Sweet Bell Peppers (Tricolor Pack)',
        brand: 'Sunset Farms',
        category: 'Produce' as const,
        suggestedLocationId: 'loc-fridge',
        quantity: 3,
        unit: 'pcs pack',
        estimatedShelfLifeDays: 9,
        confidence: 0.93,
        expirationDate: getDateOffset(9),
        estimatedPrice: 3.89,
        selected: true,
        matchedGrocyProductName: 'Sweet Bell Peppers',
      },
      {
        id: 'det-4',
        name: 'Oatly Oat Milk Barista Edition',
        brand: 'Oatly',
        category: 'Beverages' as const,
        suggestedLocationId: 'loc-fridge',
        quantity: 1,
        unit: 'Carton (1L)',
        estimatedShelfLifeDays: 45,
        confidence: 0.97,
        expirationDate: getDateOffset(45),
        estimatedPrice: 4.29,
        selected: true,
        matchedGrocyProductName: 'Oat Milk Barista',
      },
    ],
  },
  {
    title: '🧊 Refrigerator Shelf Check',
    summary: 'Detected dairy, condiments, and greens in the refrigerator.',
    items: [
      {
        id: 'det-5',
        name: 'Organic Whole Milk',
        brand: 'Horizon',
        category: 'Dairy & Eggs' as const,
        suggestedLocationId: 'loc-fridge',
        quantity: 1,
        unit: 'Gallon',
        estimatedShelfLifeDays: 7,
        confidence: 0.95,
        expirationDate: getDateOffset(7),
        estimatedPrice: 4.89,
        selected: true,
        matchedGrocyProductId: 101,
        matchedGrocyProductName: 'Organic Whole Milk',
      },
      {
        id: 'det-6',
        name: 'Kerrygold Pure Irish Butter',
        brand: 'Kerrygold',
        category: 'Dairy & Eggs' as const,
        suggestedLocationId: 'loc-fridge',
        quantity: 2,
        unit: 'Blocks (250g)',
        estimatedShelfLifeDays: 60,
        confidence: 0.98,
        expirationDate: getDateOffset(60),
        estimatedPrice: 5.25,
        selected: true,
        matchedGrocyProductName: 'Irish Butter',
      },
      {
        id: 'det-7',
        name: 'Organic Baby Spinach',
        brand: 'Earthbound Farm',
        category: 'Produce' as const,
        suggestedLocationId: 'loc-fridge',
        quantity: 1,
        unit: 'Clamshell (300g)',
        estimatedShelfLifeDays: 6,
        confidence: 0.92,
        expirationDate: getDateOffset(6),
        estimatedPrice: 3.49,
        selected: true,
        matchedGrocyProductId: 105,
        matchedGrocyProductName: 'Organic Baby Spinach',
      },
    ],
  },
  {
    title: '🧾 Grocery Supermarket Receipt Scan',
    summary: 'Receipt parsed from Trader Joe\'s / Whole Foods with 4 items & pricing.',
    items: [
      {
        id: 'det-8',
        name: 'Vital Farms Pasture Eggs 12ct',
        brand: 'Vital Farms',
        category: 'Dairy & Eggs' as const,
        suggestedLocationId: 'loc-fridge',
        quantity: 1,
        unit: 'Dozen (12pcs)',
        estimatedShelfLifeDays: 25,
        confidence: 0.99,
        expirationDate: getDateOffset(25),
        estimatedPrice: 5.99,
        selected: true,
        matchedGrocyProductId: 102,
        matchedGrocyProductName: 'Pasture-Raised Large Eggs',
      },
      {
        id: 'det-9',
        name: 'Hass Avocados 4-Pack',
        brand: 'Organic',
        category: 'Produce' as const,
        suggestedLocationId: 'loc-counter',
        quantity: 4,
        unit: 'pcs',
        estimatedShelfLifeDays: 5,
        confidence: 0.94,
        expirationDate: getDateOffset(5),
        estimatedPrice: 4.49,
        selected: true,
        matchedGrocyProductId: 104,
        matchedGrocyProductName: 'Fresh Hass Avocados',
      },
      {
        id: 'det-10',
        name: 'San Marzano Peeled Tomatoes',
        brand: 'Rego DOP',
        category: 'Canned Goods' as const,
        suggestedLocationId: 'loc-pantry',
        quantity: 2,
        unit: 'Cans (800g)',
        estimatedShelfLifeDays: 365,
        confidence: 0.96,
        expirationDate: getDateOffset(365),
        estimatedPrice: 4.25,
        selected: true,
        matchedGrocyProductId: 111,
        matchedGrocyProductName: 'San Marzano Canned Tomatoes',
      },
    ],
  },
];
